(function () {
  'use strict';

  // The script may be emitted from multiple Liquid snippets on the same page
  // (PDP form snippet, plus the cart-item snippet for any DOB-needing line).
  // Browsers fetch once (URL cache) but execute every <script> tag, so guard
  // here to prevent duplicate event-listener registration and observer setup.
  if (window.__dobCollectionLoaded) return;
  window.__dobCollectionLoaded = true;

  const SELECTORS = {
    form: '[data-dob-form]',
    field: '[data-dob-field]',
    error: '[data-dob-error]',
    buyFieldset: '[data-dob-buy-fieldset]',
    requiredNote: '[data-dob-required-note]',
    slots: '[data-dob-slots]',
    slotLabel: '[data-dob-slot-label]',
    sub: '[data-dob-sub]',
  };

  const slotCountCache = new Map();
  const formWriteStates = new WeakMap();
  let cartLineSnapshot = new Map();
  let cartGcTimer = null;
  let lastSubmittedDobForm = null;
  let pendingCartAttributeCleanup = {};

  const CART_ATTR_KEY_RE = /^(Customer name|Date of birth)( \(Test \d+\))? \[([^\]]+)\]$/;
  const LINE_HASH_RE = /^[a-f0-9]{8}$/i;

  const MESSAGES = {
    nameEmpty: "Please enter the patient's name.",
    dobEmpty: 'Please enter a date of birth.',
    dobInvalid: 'Please enter a valid date of birth between 1900 and today.',
  };

  const SUB_COPY = {
    single: 'Your name and date of birth are needed by the lab for the screening form that accompanies your sample.',
    multi: 'Each test needs its own name and date of birth for the lab form that accompanies the sample.',
  };

  function clarityEvent(name, properties) {
    if (typeof window.clarity !== 'function') return;
    try {
      window.clarity('event', name, properties || {});
    } catch (e) {
      // Silent — Clarity failures must not break the form.
    }
  }

  function buildEventProperties(formEl) {
    const fields = Array.from(formEl.querySelectorAll(SELECTORS.field));
    const filled = fields.filter(function (f) { return (f.value || '').trim(); }).length;
    return {
      placement: formEl.dataset.dobContext,
      product_handle: formEl.dataset.dobProductHandle,
      slots_needed: Math.max(1, fields.length / 2),
      slots_filled: Math.floor(filled / 2),
      mode: formEl.dataset.dobRequired === 'true' ? 'required' : 'optional',
    };
  }

  function getFormWriteState(formEl) {
    let state = formWriteStates.get(formEl);
    if (!state) {
      state = {
        inFlight: null,
        pendingPayload: null,
        debounceTimer: null,
        promoted: false,
        lineKey: null,
        previousResolvedIdentifier: resolveIdentifier(formEl.dataset.dobProductSku, formEl.dataset.dobProductHandle),
        previousIdentifierKind: resolveIdentifierKind(formEl.dataset.dobProductSku),
      };
      formWriteStates.set(formEl, state);
    }
    return state;
  }

  function resolveIdentifier(sku, handle) {
    return (sku || '').trim() || (handle || '').trim();
  }

  function resolveIdentifierKind(sku) {
    return (sku || '').trim() ? 'sku' : 'handle';
  }

  function shortLineHash(lineKey) {
    const key = String(lineKey || '');
    return key.slice(-8);
  }

  function attributeSuffix(sku, handle, lineKey) {
    const identifier = resolveIdentifier(sku, handle);
    if (!identifier) return '';
    if (!lineKey) return ' [' + identifier + ']';
    return ' [' + identifier + ' #' + shortLineHash(lineKey) + ']';
  }

  function buildCartAttributesForLine(opts) {
    const attrs = {};
    const properties = opts.properties || {};
    const scopedSuffix = attributeSuffix(opts.sku, opts.handle, opts.lineKey);
    if (!scopedSuffix) return attrs;

    Object.keys(properties).forEach(function (key) {
      attrs[key + scopedSuffix] = properties[key];
    });

    if (opts.lineKey) {
      const skuOnlySuffix = attributeSuffix(opts.sku, opts.handle, null);
      Object.keys(properties).forEach(function (key) {
        attrs[key + skuOnlySuffix] = '';
      });
    }

    return attrs;
  }

  function postCartAttributes(attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart update responded ' + res.status);
      return res;
    });
  }

  function sendBeaconCartAttributes(attributes) {
    if (!navigator.sendBeacon) return false;
    const blob = new Blob([JSON.stringify({ attributes: attributes })], { type: 'application/json' });
    return navigator.sendBeacon('/cart/update.js', blob);
  }

  function writeCartAttributesForLine(opts) {
    return postCartAttributes(buildCartAttributesForLine(opts));
  }

  function requestQueuedCartAttributeWrite(formEl, payload) {
    const state = getFormWriteState(formEl);
    let effectivePayload = payload;
    if (state.promoted) {
      effectivePayload = Object.assign({}, payload, {
        sku: formEl.dataset.dobProductSku,
        handle: formEl.dataset.dobProductHandle,
        lineKey: state.lineKey,
      });
    }

    if (!state.inFlight) {
      state.inFlight = writeCartAttributesForLine(effectivePayload).then(function (res) {
        markPayloadFieldsClean(effectivePayload);
        return res;
      }).catch(function (err) {
        markPayloadFieldsDirty(effectivePayload);
        console.warn('[dob-collection] cart attribute write failed:', err);
      }).finally(function () {
        state.inFlight = null;
        if (state.pendingPayload) {
          const next = state.pendingPayload;
          state.pendingPayload = null;
          requestQueuedCartAttributeWrite(formEl, next);
        }
      });
      return;
    }

    state.pendingPayload = effectivePayload;
  }

  function markPayloadFieldsDirty(payload) {
    (payload.fields || []).forEach(function (field) {
      field.dataset.dobDirty = 'true';
    });
  }

  function markPayloadFieldsClean(payload) {
    (payload.fields || []).forEach(function (field) {
      field.dataset.dobDirty = 'false';
    });
  }

  function isFieldValueValid(field) {
    const type = field.dataset.dobField;
    const value = (field.value || '').trim();
    if (!value) return true;
    if (type === 'name') return true;
    if (type === 'dob') {
      const parsed = new Date(value);
      const min = new Date('1900-01-01');
      const max = new Date();
      max.setHours(23, 59, 59, 999);
      return !isNaN(parsed.getTime()) && parsed >= min && parsed <= max;
    }
    return true;
  }

  // Pure check: true iff every visible DOB field has a non-empty, valid value.
  // Does NOT mutate field state (validateField has side effects — aria-invalid,
  // error UI — and we don't want those on every keystroke).
  function areAllDobFieldsFilledAndValid(formEl) {
    const fields = Array.from(formEl.querySelectorAll(SELECTORS.field));
    if (fields.length === 0) return false;
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      const value = (f.value || '').trim();
      if (!value) return false;
      if (!isFieldValueValid(f)) return false;
    }
    return true;
  }

  // Disables the express-checkout area when required-mode DOB is incomplete.
  //
  // Why this is needed even though we have a click intercept: branded Apple Pay
  // and Google Pay buttons render inside cross-origin iframes. Parent JS cannot
  // capture clicks inside those iframes, so the existing click intercept never
  // sees them. But we CAN set the `inert` attribute on the iframe's parent
  // container; the browser then refuses to deliver events to anything inside,
  // including the iframe's content. This closes the iframe bypass without
  // crossing the same-origin boundary.
  //
  // Scope: only the express containers (.shopify-payment-button and the
  // additional-checkout-buttons wrapper). The regular Add-to-Cart button stays
  // interactive — its click intercept gives a better UX (focus first invalid
  // field) than the inert "nothing happens" path.
  //
  // Encouragement-first mode: no-op. The customer is allowed to skip DOB.
  function syncPaymentAreaInertState(formEl) {
    const required = formEl.dataset.dobRequired === 'true';
    const productForm = document.getElementById(formEl.dataset.dobFormId);
    if (!productForm) return;
    const fieldset = productForm.querySelector(SELECTORS.buyFieldset);
    if (!fieldset) return;

    // [data-dob-express-lock] is the Liquid-rendered wrapper that ships locked
    // (inert + pointer-events:none inline) so the buttons are dead from first
    // paint; this function is the only thing that unlocks it.
    const expressContainers = fieldset.querySelectorAll(
      '[data-dob-express-lock], .shopify-payment-button, .additional-checkout-buttons'
    );
    if (!expressContainers.length) return;

    const attributesReconciled = formEl.dataset.dobCartAttrsReconciled === 'true';
    const shouldLock = required && (
      !attributesReconciled || !areAllDobFieldsFilledAndValid(formEl)
    );

    expressContainers.forEach(function (container) {
      if (shouldLock) {
        container.inert = true;
        container.setAttribute('aria-disabled', 'true');
        container.style.opacity = '0.5';
        container.style.cursor = 'not-allowed';
        // Belt and braces for browsers/webviews that ignore `inert`.
        container.style.pointerEvents = 'none';
      } else {
        container.inert = false;
        container.removeAttribute('aria-disabled');
        container.style.opacity = '';
        container.style.cursor = '';
        container.style.pointerEvents = '';
      }
    });
  }

  function propNameFromField(field) {
    const match = String(field.name || '').match(/^properties\[(.+)\]$/);
    return match ? match[1] : field.name;
  }

  function collectPdpCartAttributeProperties(formEl) {
    const properties = {};
    const fields = [];
    Array.from(formEl.querySelectorAll(SELECTORS.field)).forEach(function (field) {
      const value = (field.value || '').trim();
      if (value && !isFieldValueValid(field)) return;
      properties[propNameFromField(field)] = value;
      fields.push(field);
    });
    return { properties: properties, fields: fields };
  }

  function flushPdpCartAttributeWrite(formEl) {
    const collected = collectPdpCartAttributeProperties(formEl);
    if (!Object.keys(collected.properties).length) return;
    const state = getFormWriteState(formEl);
    const payload = {
      sku: formEl.dataset.dobProductSku,
      handle: formEl.dataset.dobProductHandle,
      lineKey: state.promoted ? state.lineKey : null,
      properties: collected.properties,
      fields: collected.fields,
    };
    markPayloadFieldsDirty(payload);
    requestQueuedCartAttributeWrite(formEl, payload);
  }

  function schedulePdpCartAttributeWrite(formEl) {
    const state = getFormWriteState(formEl);
    if (state.debounceTimer) window.clearTimeout(state.debounceTimer);
    state.debounceTimer = window.setTimeout(function () {
      state.debounceTimer = null;
      flushPdpCartAttributeWrite(formEl);
    }, 200);
  }

  function flushPdpCartAttributeWriteNow(formEl) {
    const state = getFormWriteState(formEl);
    if (state.debounceTimer) {
      window.clearTimeout(state.debounceTimer);
      state.debounceTimer = null;
    }
    flushPdpCartAttributeWrite(formEl);
  }

  function promotePdpFormAfterAdd(formEl, addedProduct) {
    const lineKey = addedProduct && addedProduct.key;
    if (!lineKey) return;

    const state = getFormWriteState(formEl);
    state.promoted = true;
    state.lineKey = lineKey;
    state.pendingPayload = null;
    state.previousResolvedIdentifier = resolveIdentifier(formEl.dataset.dobProductSku, formEl.dataset.dobProductHandle);
    state.previousIdentifierKind = resolveIdentifierKind(formEl.dataset.dobProductSku);

    const collected = collectPdpCartAttributeProperties(formEl);
    if (!Object.keys(collected.properties).length) return;

    const attributes = buildCartAttributesForLine({
      sku: formEl.dataset.dobProductSku,
      handle: formEl.dataset.dobProductHandle,
      lineKey: lineKey,
      properties: collected.properties,
    });

    if (!sendBeaconCartAttributes(attributes)) {
      // Rare fallback: this may run outside the single-flight queue, but only
      // when sendBeacon refuses the tiny upgrade payload.
      writeCartAttributesForLine({
        sku: formEl.dataset.dobProductSku,
        handle: formEl.dataset.dobProductHandle,
        lineKey: lineKey,
        properties: collected.properties,
      }).catch(function (err) {
        console.warn('[dob-collection] post-add DOB attribute upgrade failed:', err);
      });
    }
  }

  function observeForViewed(formEl) {
    if (!('IntersectionObserver' in window)) {
      clarityEvent('dob_form_viewed', buildEventProperties(formEl));
      return;
    }
    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          clarityEvent('dob_form_viewed', buildEventProperties(formEl));
          observer.disconnect();
        }
      });
    }, { threshold: 0.25 });
    observer.observe(formEl);
  }

  function init(formEl) {
    if (formEl.dataset.dobInitialized === 'true') return;
    formEl.dataset.dobInitialized = 'true';
    getFormWriteState(formEl);

    observeForViewed(formEl);

    const required = formEl.dataset.dobRequired === 'true';
    const formId = formEl.dataset.dobFormId;
    const productForm = document.getElementById(formId);
    const buyFieldset = productForm && productForm.querySelector(SELECTORS.buyFieldset);

    wireFields(formEl, productForm, buyFieldset, required);
    attachVariantChangeListener(formEl);
    formEl.dataset.dobCartAttrsReconciled = 'false';
    gcOrphanSkuOnlyAttrs({
      protectedSku: formEl.dataset.dobProductSku,
      protectedHandle: formEl.dataset.dobProductHandle,
    }).then(function (reconciled) {
      if (reconciled) formEl.dataset.dobCartAttrsReconciled = 'true';
      syncPaymentAreaInertState(formEl);
    });

    if (required && buyFieldset) {
      attachClickInterceptor(formEl, buyFieldset);
    }

    // Set the express-checkout lock state from the moment the form is wired up.
    // In required mode with empty fields, this immediately makes Apple Pay /
    // Google Pay (iframe-bound, otherwise un-interceptable) inert.
    syncPaymentAreaInertState(formEl);

    function applyResolvedSlotCount(perUnit) {
      // Slot count on PDP is perUnit × current quantity. Default to qty=1
      // when no quantity selector is on the template (current FIT setup).
      const qtyInput = productForm && productForm.querySelector('[name="quantity"]');
      const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      const totalSlots = perUnit * qty;
      if (totalSlots > 1) {
        setSlotCount(formEl, totalSlots, required);
        wireFields(formEl, productForm, buyFieldset, required);
        // Newly-added slots are empty by construction; re-sync the express
        // lock so a "valid" state from before expansion (e.g., slot 1 happened
        // to be filled fast) is downgraded back to "locked" until the new
        // slots get filled too.
        syncPaymentAreaInertState(formEl);
      }
    }

    resolveSlotCount(formEl.dataset.dobProductHandle, formEl.dataset.dobStorefrontToken).then(function (count) {
      formEl.dataset.dobSlotsResolved = 'true';
      clearResolvingNotice(formEl);
      applyResolvedSlotCount(count);
    }).catch(function (err) {
      console.warn('[dob-collection] slot count resolution failed, falling back to 1 slot:', err);
      // Per design: unblock on error rather than locking the customer out.
      // Risks letting a real bundle slip through if the Storefront API is
      // unreachable, but the alternative (forever-locked checkout on error)
      // is worse for the long tail.
      formEl.dataset.dobSlotsResolved = 'true';
      clearResolvingNotice(formEl);
    });

    // Quantity-selector listener: when a PDP template exposes a quantity
    // selector, slot count must track perUnit × qty so the customer fills
    // out one set per kit. Event delegation handles themes that re-render
    // the quantity input on variant change.
    if (productForm) {
      function resyncSlotsFromQty() {
        const qtyInput = productForm.querySelector('[name="quantity"]');
        if (!qtyInput) return;
        const perUnit = slotCountCache.get(formEl.dataset.dobProductHandle);
        if (perUnit == null) return; // Storefront API hasn't resolved yet
        const qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
        setSlotCount(formEl, perUnit * qty, required);
        wireFields(formEl, productForm, buyFieldset, required);
        // qty change can add empty slots — re-evaluate the express lock state.
        syncPaymentAreaInertState(formEl);
      }

      productForm.addEventListener('change', function (event) {
        if (!event.target || !event.target.matches || !event.target.matches('[name="quantity"]')) return;
        resyncSlotsFromQty();
      });

      // Impulse's theme.QtySelector (theme.js:3068) updates input.value
      // directly on plus/minus button click without dispatching a `change`
      // event, so the listener above doesn't fire for the +/- path. Listen
      // for clicks on the theme's selector buttons too. QtySelector's own
      // click handler is on the button itself (target phase), so this
      // bubble-phase listener runs after input.value is already updated.
      productForm.addEventListener('click', function (event) {
        if (!event.target || !event.target.closest) return;
        if (!event.target.closest('.js-qty__adjust--plus, .js-qty__adjust--minus')) return;
        resyncSlotsFromQty();
      });
    }
  }

  function attachClickInterceptor(formEl, buyFieldset) {
    if (buyFieldset.dataset.dobClickIntercepted === 'true') return;
    buyFieldset.dataset.dobClickIntercepted = 'true';

    buyFieldset.addEventListener('click', function (event) {
      const target = event.target;
      // Only intercept clicks on actual buttons / checkout triggers inside the fieldset.
      const isCheckoutTrigger = target.closest('button, [role="button"], shop-pay-button, [is^="shopify-"]');
      if (!isCheckoutTrigger) return;

      // Block while slot count is unresolved. Without this, a fast click on
      // a bundle product validates only the single pre-resolution slot and
      // submits incomplete DOB data. (See feature/dob-collection F1 fix.)
      if (formEl.dataset.dobSlotsResolved !== 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        showResolvingNotice(formEl);
        clarityEvent('dob_form_validation_failed', buildEventProperties(formEl));
        return;
      }

      if (!validateAllFields(formEl)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        clarityEvent('dob_form_validation_failed', buildEventProperties(formEl));
        focusFirstInvalid(formEl);
      }
    }, true); // capture phase: runs before the button's own click handlers
  }

  function attachVariantChangeListener(formEl) {
    if (formEl.dataset.dobVariantListenerWired === 'true') return;
    formEl.dataset.dobVariantListenerWired = 'true';

    const section = formEl.closest('[data-section-id]') || formEl.closest('[id^="shopify-section-"]');
    if (!section) return;

    section.addEventListener('variantChange', function (event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;

      const state = getFormWriteState(formEl);
      const previousId = state.previousResolvedIdentifier;
      const previousKind = state.previousIdentifierKind;
      const newSku = variant.sku || '';
      formEl.dataset.dobProductSku = newSku;
      const nextId = resolveIdentifier(newSku, formEl.dataset.dobProductHandle);
      const nextKind = resolveIdentifierKind(newSku);

      // Slot-count changes by variant are not expected for current FIT bundles.
      // If that changes, also clear higher-slot SKU-only keys here.
      if (!state.promoted && previousId && nextId !== previousId) {
        const properties = {};
        Array.from(formEl.querySelectorAll(SELECTORS.field)).forEach(function (field) {
          properties[propNameFromField(field)] = '';
        });
        const cleanupPayload = {
          sku: previousKind === 'sku' ? previousId : '',
          handle: previousKind === 'sku' ? formEl.dataset.dobProductHandle : previousId,
          lineKey: null,
          properties: properties,
        };
        const afterInFlight = state.inFlight || Promise.resolve();
        afterInFlight.then(function () {
          return writeCartAttributesForLine(cleanupPayload);
        }).catch(function (err) {
          console.warn('[dob-collection] previous variant DOB attribute cleanup failed:', err);
        });
      }

      state.previousResolvedIdentifier = nextId;
      state.previousIdentifierKind = nextKind;

      // Variant change may re-render the payment button DOM in some Shopify
      // setups (e.g., Shop Pay reinitialises). Re-apply the inert lock so the
      // fresh DOM picks up the current required-mode state.
      syncPaymentAreaInertState(formEl);
    });
  }

  function validateAllFields(formEl) {
    const allFields = Array.from(formEl.querySelectorAll(SELECTORS.field));
    let allValid = true;
    // forEach (not every) so we call validateField on every field — all errors light up at once.
    allFields.forEach(function (f) {
      if (!validateField(f)) allValid = false;
    });
    return allValid;
  }

  function focusFirstInvalid(formEl) {
    const firstInvalid = formEl.querySelector(SELECTORS.field + '[aria-invalid="true"]');
    if (firstInvalid) firstInvalid.focus();
  }

  function wireFields(formEl, productForm, buyFieldset, required) {
    const fields = Array.from(formEl.querySelectorAll(SELECTORS.field));
    const focusFlag = 'dobFocusReported';

    fields.forEach(function (field) {
      if (field.dataset.dobWired === 'true') return;
      field.dataset.dobWired = 'true';

      field.addEventListener('focus', function () {
        if (formEl.dataset[focusFlag] === 'true') return;
        formEl.dataset[focusFlag] = 'true';
        clarityEvent('dob_field_focused', buildEventProperties(formEl));
      });

      field.addEventListener('blur', function () {
        validateField(field);
        flushPdpCartAttributeWriteNow(formEl);
        syncPaymentAreaInertState(formEl);
        const completed = field.getAttribute('aria-invalid') !== 'true' && (field.value || '').trim();
        if (completed) {
          clarityEvent('dob_field_completed', Object.assign(buildEventProperties(formEl), {
            field: field.dataset.dobField,
            slot_index: parseInt(field.dataset.dobSlotIndex, 10),
          }));
        }
      });
      field.addEventListener('input', function () {
        clearFieldError(field);
        schedulePdpCartAttributeWrite(formEl);
        syncPaymentAreaInertState(formEl);
      });
      field.addEventListener('change', function () {
        schedulePdpCartAttributeWrite(formEl);
        syncPaymentAreaInertState(formEl);
      });
    });

    if (productForm && productForm.dataset.dobSubmitWired !== 'true') {
      productForm.dataset.dobSubmitWired = 'true';

      productForm.addEventListener('formdata', function (event) {
        Array.from(formEl.querySelectorAll(SELECTORS.field)).forEach(function (f) {
          if (!(f.value || '').trim()) {
            event.formData.delete(f.name);
          }
        });
      });

      // Blocking lives on a capture-phase document listener. Form-level
      // listeners (e.g. Impulse's AjaxProduct.addItemFromForm at theme.js:2799)
      // register in bubble phase and don't check evt.defaultPrevented, so a
      // form-level preventDefault would not stop the cart POST. Capture-phase
      // on document runs strictly before any form-level listener regardless
      // of registration order; stopImmediatePropagation then kills the rest.
      document.addEventListener('submit', function (event) {
        if (event.target !== productForm) return;

        if (required) {
          if (formEl.dataset.dobSlotsResolved !== 'true') {
            event.preventDefault();
            event.stopImmediatePropagation();
            showResolvingNotice(formEl);
            clarityEvent('dob_form_validation_failed', buildEventProperties(formEl));
            return;
          }
          if (!validateAllFields(formEl)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            clarityEvent('dob_form_validation_failed', buildEventProperties(formEl));
            focusFirstInvalid(formEl);
            return;
          }
        }

        lastSubmittedDobForm = formEl;
      }, true);

      // Reaching this bubble-phase listener means the capture-phase guard
      // above did not block, so the submit is going through. Emit success
      // analytics only.
      productForm.addEventListener('submit', function () {
        const allFields = Array.from(formEl.querySelectorAll(SELECTORS.field));
        const fieldsFilled = allFields.filter(function (f) { return (f.value || '').trim(); }).length;
        if (required || fieldsFilled > 0) {
          clarityEvent('dob_form_submitted', buildEventProperties(formEl));
        }
      });
    }
  }

  function resolveSlotCount(handle, token) {
    if (!handle || !token) return Promise.resolve(1);
    if (slotCountCache.has(handle)) return Promise.resolve(slotCountCache.get(handle));

    const query = 'query DobBundleSize($h: String!) { product(handle: $h) { selectedOrFirstAvailableVariant { requiresComponents components(first: 50) { nodes { quantity } } } } }';

    return fetch('/api/2024-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
        'Accept': 'application/json',
      },
      body: JSON.stringify({ query: query, variables: { h: handle } }),
    }).then(function (res) {
      if (!res.ok) throw new Error('Storefront API responded ' + res.status);
      return res.json();
    }).then(function (data) {
      // GraphQL returns HTTP 200 even on query/permission errors — surface
      // them in the console instead of silently falling back to 1 slot, which
      // would under-collect DOB for real bundles without anyone noticing.
      if (data && Array.isArray(data.errors) && data.errors.length) {
        console.warn('[dob-collection] Storefront API returned errors for handle "' + handle + '":', data.errors);
      }
      const variant = data && data.data && data.data.product && data.data.product.selectedOrFirstAvailableVariant;
      let count = 1;
      if (variant && variant.requiresComponents) {
        const nodes = (variant.components && variant.components.nodes) || [];
        const total = nodes.reduce(function (sum, n) { return sum + (n.quantity || 0); }, 0);
        if (total > 0) count = total;
      }
      slotCountCache.set(handle, count);
      return count;
    });
  }

  // Bidirectional slot sizing on the PDP form. Slot 1 stays in place with its
  // invariant 'Customer name' / 'Date of birth' names (see slotKeys). Higher
  // slots are cloned in or removed to match the target count. Idempotent at a
  // given count, so it's safe to call repeatedly when qty selectors change.
  function setSlotCount(formEl, count, required) {
    const slotsContainer = formEl.querySelector(SELECTORS.slots);
    if (!slotsContainer) return;

    const subEl = formEl.querySelector(SELECTORS.sub);
    if (subEl) subEl.textContent = count > 1 ? SUB_COPY.multi : SUB_COPY.single;

    const slot1 = slotsContainer.querySelector('[data-dob-slot="1"]');
    if (!slot1) return;
    const legend1 = slot1.querySelector(SELECTORS.slotLabel);
    if (legend1) {
      if (count > 1) {
        legend1.textContent = 'Test 1 of ' + count;
        legend1.hidden = false;
      } else {
        legend1.textContent = '';
        legend1.hidden = true;
      }
    }

    // Remove any existing slots beyond the target count (qty was decreased).
    Array.from(slotsContainer.querySelectorAll('[data-dob-slot]')).forEach(function (el) {
      const idx = parseInt(el.getAttribute('data-dob-slot'), 10);
      if (idx > count) el.remove();
    });

    // Find current highest slot index after possible removals — clone from
    // there up to `count`. Avoids duplicating slots when this runs repeatedly.
    let currentMax = 0;
    slotsContainer.querySelectorAll('[data-dob-slot]').forEach(function (el) {
      const idx = parseInt(el.getAttribute('data-dob-slot'), 10);
      if (idx > currentMax) currentMax = idx;
    });

    for (let i = currentMax + 1; i <= count; i++) {
      const clone = slot1.cloneNode(true);
      clone.setAttribute('data-dob-slot', String(i));

      const formIdSuffix = formEl.dataset.dobFormId;
      const newNameId = 'dob-name-' + i + '-' + formIdSuffix;
      const newDobId = 'dob-dob-' + i + '-' + formIdSuffix;

      const labels = clone.querySelectorAll('label');
      const inputs = clone.querySelectorAll('input');
      const errors = clone.querySelectorAll(SELECTORS.error);
      const legend = clone.querySelector(SELECTORS.slotLabel);

      inputs.forEach(function (input) {
        input.value = '';
        input.removeAttribute('aria-invalid');
        input.dataset.dobWired = 'false';
        input.dataset.dobSlotIndex = String(i);
        const fieldType = input.dataset.dobField;
        if (fieldType === 'name') {
          input.id = newNameId;
          input.name = 'properties[Customer name (Test ' + i + ')]';
        } else if (fieldType === 'dob') {
          input.id = newDobId;
          input.name = 'properties[Date of birth (Test ' + i + ')]';
        }
        if (!required) input.removeAttribute('required');
      });

      labels.forEach(function (label) {
        const forAttr = label.getAttribute('for');
        if (forAttr && forAttr.indexOf('dob-name-') === 0) label.setAttribute('for', newNameId);
        if (forAttr && forAttr.indexOf('dob-dob-') === 0) label.setAttribute('for', newDobId);
      });

      errors.forEach(function (errEl) {
        errEl.textContent = '';
        errEl.hidden = true;
      });

      if (legend) {
        legend.textContent = 'Test ' + i + ' of ' + count;
        legend.hidden = false;
      }

      slotsContainer.appendChild(clone);
    }

    // Refresh legends on slots 2..N in case `count` changed without us
    // cloning new slots (e.g., qty went 3→2: slot 2's legend still reads
    // 'Test 2 of 3'). Slot 1's legend already updated above.
    slotsContainer.querySelectorAll('[data-dob-slot]').forEach(function (slot) {
      const idx = parseInt(slot.getAttribute('data-dob-slot'), 10);
      if (idx < 2) return;
      const legend = slot.querySelector(SELECTORS.slotLabel);
      if (legend) {
        legend.textContent = 'Test ' + idx + ' of ' + count;
        legend.hidden = false;
      }
    });
  }

  function validateField(field) {
    const type = field.dataset.dobField;
    const value = (field.value || '').trim();

    if (type === 'name') {
      if (!value) return setFieldError(field, MESSAGES.nameEmpty);
      return clearFieldError(field);
    }

    if (type === 'dob') {
      if (!value) return setFieldError(field, MESSAGES.dobEmpty);
      const parsed = new Date(value);
      const min = new Date('1900-01-01');
      const max = new Date();
      max.setHours(23, 59, 59, 999);
      if (isNaN(parsed.getTime()) || parsed < min || parsed > max) {
        return setFieldError(field, MESSAGES.dobInvalid);
      }
      return clearFieldError(field);
    }

    return true;
  }

  function setFieldError(field, message) {
    field.setAttribute('aria-invalid', 'true');
    const errEl = field.parentElement.querySelector(SELECTORS.error);
    if (errEl) {
      errEl.textContent = message;
      errEl.hidden = false;
    }
    return false;
  }

  function clearFieldError(field) {
    field.removeAttribute('aria-invalid');
    const errEl = field.parentElement.querySelector(SELECTORS.error);
    if (errEl) {
      errEl.textContent = '';
      errEl.hidden = true;
    }
    return true;
  }

  function findUniqueDobFormForVariant(variantId) {
    if (!variantId) return null;

    const candidates = Array.from(document.querySelectorAll(SELECTORS.form + '[data-dob-context="pdp"]')).filter(function (formEl) {
      const productForm = document.getElementById(formEl.dataset.dobFormId);
      const variantInput = productForm && productForm.querySelector('[name="id"]');
      return variantInput && String(variantInput.value) === String(variantId);
    });

    return candidates.length === 1 ? candidates[0] : null;
  }

  function findRecordedDobFormForAddedProduct(addedProduct) {
    const recordedForm = lastSubmittedDobForm;
    lastSubmittedDobForm = null;

    if (!addedProduct) return null;

    if (recordedForm && document.contains(recordedForm)) {
      const productForm = document.getElementById(recordedForm.dataset.dobFormId);
      const variantInput = productForm && productForm.querySelector('[name="id"]');
      if (variantInput && String(variantInput.value) === String(addedProduct.id)) {
        return recordedForm;
      }
    }

    return findUniqueDobFormForVariant(addedProduct.id);
  }

  function findPdpFormForSubmittedEvent(event, addedProduct) {
    const innerForm = event.target && event.target.querySelector
      ? event.target.querySelector('form')
      : null;
    const formId = innerForm && innerForm.id;
    const formEl = formId
      ? document.querySelector(SELECTORS.form + '[data-dob-context="pdp"][data-dob-form-id="' + formId + '"]')
      : null;

    return formEl || findUniqueDobFormForVariant(addedProduct && addedProduct.id);
  }

  function bootstrap() {
    document.querySelectorAll(SELECTORS.form).forEach(init);
  }

  function bootstrapCart() {
    document.querySelectorAll('[data-dob-cart-line]').forEach(initCartLine);
    document.querySelectorAll('[data-dob-cart-checkout-wrap]').forEach(attachCheckoutIntercept);
    syncCartLineSnapshot();
    // Apply the express-checkout lock based on current line states.
    // Runs after initCartLine has had a chance to set initial states, but
    // initCartLine's slot-resolution is async — so the FIRST sync here may
    // see lines still in "loading" state (counted as incomplete → locked),
    // which is correct. renderCartLineState (below) re-syncs once each line
    // resolves to its real state.
    syncAllCartCheckoutWrappers();
  }

  function scheduleCartAttributeGc() {
    if (cartGcTimer) window.clearTimeout(cartGcTimer);
    cartGcTimer = window.setTimeout(function () {
      cartGcTimer = null;
      gcOrphanSkuOnlyAttrs(detectActivePdpProtection());
    }, 250);
  }

  function initCartLine(lineEl) {
    if (lineEl.dataset.dobLineInitialized === 'true') return;
    lineEl.dataset.dobLineInitialized = 'true';

    const handle = lineEl.dataset.dobLineHandle;
    const token = lineEl.dataset.dobStorefrontToken;
    const quantity = parseInt(lineEl.dataset.dobLineQuantity, 10) || 1;

    resolveSlotCount(handle, token).then(function (perUnit) {
      const slotsNeeded = perUnit * quantity;
      renderCartLineState(lineEl, slotsNeeded);
      syncCartLineSnapshot();
    }).catch(function (err) {
      console.warn('[dob-collection] cart slot resolution failed:', err);
      renderCartLineState(lineEl, quantity);
      syncCartLineSnapshot();
    });
  }

  function arrayToPropMap(propsArr) {
    const map = {};
    (propsArr || []).forEach(function (entry) {
      if (Array.isArray(entry) && entry.length >= 2) map[entry[0]] = entry[1];
    });
    return map;
  }

  // Slot 1 always uses the unindexed keys regardless of total slot count.
  // That makes quantity growth in the cart non-destructive: slot 1's keys
  // stay stable so existing data is still found, and only new slots get
  // '(Test N)' suffixes. Slot 2..N always indexed.
  function slotKeys(slotIndex) {
    if (slotIndex === 1) {
      return { name: 'Customer name', dob: 'Date of birth' };
    }
    return {
      name: 'Customer name (Test ' + slotIndex + ')',
      dob: 'Date of birth (Test ' + slotIndex + ')',
    };
  }

  // Read a slot's existing values from line-item properties, with a backward-
  // compat fallback for legacy multi-slot data: 2-pack carts written before
  // the slot-1 key unification stored slot 1 as 'Customer name (Test 1)'.
  // We still recognise that on read so the customer sees their data; the next
  // save migrates it to the unified scheme via isDobManagedKey stripping.
  function readSlotValues(props, slotIndex) {
    if (slotIndex === 1) {
      return {
        name: props['Customer name'] || props['Customer name (Test 1)'] || '',
        dob: props['Date of birth'] || props['Date of birth (Test 1)'] || '',
      };
    }
    const keys = slotKeys(slotIndex);
    return { name: props[keys.name] || '', dob: props[keys.dob] || '' };
  }

  // Inverse of slotKeys: every key this module ever writes as a line-item
  // property. Used by saveCartLineForm to strip stale DOB keys before merging
  // in the current form values, so cleared slots persist as cleared, and so
  // legacy '(Test 1)' data is removed when slot 1 is rewritten.
  const DOB_INDEXED_KEY = /^(Customer name|Date of birth) \(Test \d+\)$/;
  function isDobManagedKey(key) {
    return key === 'Customer name' || key === 'Date of birth' || DOB_INDEXED_KEY.test(key);
  }

  function parseCartAttributeKey(key) {
    const match = String(key || '').match(CART_ATTR_KEY_RE);
    if (!match) return null;
    const bracket = match[3];
    const hashIndex = bracket.lastIndexOf(' #');
    const hash = hashIndex === -1 ? '' : bracket.slice(hashIndex + 2);
    if (hashIndex === -1 || !LINE_HASH_RE.test(hash)) {
      return { propertyKey: (match[1] + (match[2] || '')), identifier: bracket, lineScoped: false };
    }
    return {
      propertyKey: (match[1] + (match[2] || '')),
      identifier: bracket.slice(0, hashIndex),
      lineScoped: true,
    };
  }

  function detectActivePdpProtection() {
    const formEl = document.querySelector(SELECTORS.form + '[data-dob-context="pdp"]');
    if (!formEl) return {};
    return {
      protectedSku: formEl.dataset.dobProductSku,
      protectedHandle: formEl.dataset.dobProductHandle,
    };
  }

  function gcOrphanSkuOnlyAttrs(opts) {
    opts = opts || {};
    // Races with PDP on-blur writes are benign: if GC clears a SKU-only key just
    // before promotion writes line-key-scoped attrs, the final state is correct;
    // if an on-blur write lands after this /cart.js read, this pass simply won't
    // see it and a later duplicate/orphan cleanup opportunity handles it.
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('cart read responded ' + res.status);
      return res.json();
    }).then(function (cart) {
      const validIdentifiers = new Set();
      (cart.items || []).forEach(function (item) {
        const sku = (item.variant && item.variant.sku) || item.sku;
        const handle = (item.product && item.product.handle) || item.handle;
        if (sku) validIdentifiers.add(String(sku));
        if (handle) validIdentifiers.add(String(handle));
      });
      if (opts.protectedSku) {
        validIdentifiers.add(String(opts.protectedSku));
      }
      if (opts.protectedHandle) {
        validIdentifiers.add(String(opts.protectedHandle));
      }

      const identifiersWithLineScopedAttrs = new Set();
      const attributes = cart.attributes || {};
      Object.keys(attributes).forEach(function (key) {
        const parsed = parseCartAttributeKey(key);
        if (parsed && parsed.lineScoped) identifiersWithLineScopedAttrs.add(parsed.identifier);
      });

      const cleanup = {};
      Object.keys(attributes).forEach(function (key) {
        const parsed = parseCartAttributeKey(key);
        if (!parsed || parsed.lineScoped) return;
        if (!validIdentifiers.has(parsed.identifier) || identifiersWithLineScopedAttrs.has(parsed.identifier)) {
          cleanup[key] = '';
        }
      });

      if (!Object.keys(cleanup).length) return true;

      Object.assign(pendingCartAttributeCleanup, cleanup);
      return postCartAttributes(cleanup).then(function () {
        Object.keys(cleanup).forEach(function (key) {
          if (pendingCartAttributeCleanup[key] === cleanup[key]) {
            delete pendingCartAttributeCleanup[key];
          }
        });
        return true;
      });
    }).catch(function (err) {
      console.warn('[dob-collection] DOB cart attribute GC failed:', err);
      return false;
    });
  }

  function beaconPendingCartAttributeCleanup() {
    if (!Object.keys(pendingCartAttributeCleanup).length) return;
    sendBeaconCartAttributes(pendingCartAttributeCleanup);
  }

  function buildCartLineSnapshot() {
    const snapshot = new Map();
    document.querySelectorAll('[data-dob-cart-line]').forEach(function (lineEl) {
      const lineKey = lineEl.dataset.dobLineKey;
      if (!lineKey) return;
      const handle = lineEl.dataset.dobLineHandle;
      const perUnit = slotCountCache.get(handle) || 1;
      const quantity = parseInt(lineEl.dataset.dobLineQuantity, 10) || 1;
      snapshot.set(lineKey, {
        lineKey: lineKey,
        sku: lineEl.dataset.dobLineSku,
        handle: handle,
        quantity: quantity,
        resolvedSlots: perUnit * quantity,
      });
    });
    return snapshot;
  }

  function cleanupCartSnapshotDiff(previous, current) {
    previous.forEach(function (oldEntry, lineKey) {
      const newEntry = current.get(lineKey);
      if (!newEntry) {
        deleteCartAttributesForLine(oldEntry, range(1, oldEntry.resolvedSlots)).catch(function (err) {
          console.warn('[dob-collection] removed-line DOB attribute cleanup failed:', err);
        });
        return;
      }
      if (newEntry.resolvedSlots < oldEntry.resolvedSlots) {
        deleteCartAttributesForLine(oldEntry, range(newEntry.resolvedSlots + 1, oldEntry.resolvedSlots)).catch(function (err) {
          console.warn('[dob-collection] reduced-quantity DOB attribute cleanup failed:', err);
        });
      }
    });
  }

  function syncCartLineSnapshot() {
    const nextSnapshot = buildCartLineSnapshot();
    cleanupCartSnapshotDiff(cartLineSnapshot, nextSnapshot);
    // Snapshot replacement is intentionally destructive once the diff is
    // computed. Cleanup POST failures are logged but not retried here; the next
    // cart render becomes the new truth.
    cartLineSnapshot = nextSnapshot;
  }

  function range(start, end) {
    const values = [];
    for (let i = start; i <= end; i++) values.push(i);
    return values;
  }

  function deleteCartAttributesForLine(entry, slotsToDelete) {
    const attributes = {};
    const suffix = attributeSuffix(entry.sku, entry.handle, entry.lineKey);
    if (!suffix) return Promise.resolve();
    (slotsToDelete || []).forEach(function (slotIndex) {
      const keys = slotKeys(slotIndex);
      attributes[keys.name + suffix] = '';
      attributes[keys.dob + suffix] = '';
    });
    return postCartAttributes(attributes);
  }

  function countFilledSlots(props, total) {
    let count = 0;
    for (let i = 1; i <= total; i++) {
      const vals = readSlotValues(props, i);
      if (vals.name.trim() && vals.dob.trim()) count++;
    }
    return count;
  }

  function formatDOB(iso) {
    if (!iso) return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const monthIdx = parseInt(parts[1], 10) - 1;
    const month = months[monthIdx] || parts[1];
    return parts[2].replace(/^0/, '') + ' ' + month + ' ' + parts[0];
  }

  function renderCartLineState(lineEl, slotsNeeded) {
    const propsScript = lineEl.querySelector('script[data-dob-line-properties]');
    const inner = lineEl.querySelector('[data-dob-cart-inner]');
    if (!propsScript || !inner) return;

    let propsArr = [];
    try { propsArr = JSON.parse(propsScript.textContent.trim()); } catch (e) { propsArr = []; }
    const props = arrayToPropMap(propsArr);

    const filledCount = countFilledSlots(props, slotsNeeded);

    inner.innerHTML = '';
    if (filledCount >= slotsNeeded) {
      inner.appendChild(buildSummary(props, slotsNeeded, lineEl));
    } else {
      inner.appendChild(buildCTA(slotsNeeded, filledCount, lineEl));
    }

    // This line transitioned state (loading→summary, loading→CTA, form→summary
    // via cancel, etc.). Re-evaluate every checkout wrapper's inert lock — a
    // single line going to summary may unlock the wrapper if it was the last
    // incomplete one; a single line dropping out of summary re-locks it.
    syncAllCartCheckoutWrappers();
  }

  function buildCTA(slotsNeeded, filledCount, lineEl) {
    const missing = slotsNeeded - filledCount;
    const wrap = document.createElement('div');
    wrap.className = 'dob-cart-item__cta';

    const heading = document.createElement('strong');
    heading.className = 'dob-cart-item__cta-heading';
    heading.textContent = 'Screening details';

    const label = document.createElement('p');
    label.className = 'dob-cart-item__cta-label';
    if (missing === slotsNeeded) {
      label.textContent = slotsNeeded === 1
        ? 'Required by the lab. Provide patient name and date of birth.'
        : 'Required by the lab. Provide patient name and date of birth per test.';
    } else {
      label.textContent = missing + ' of ' + slotsNeeded + ' test' + (slotsNeeded > 1 ? 's' : '') + ' still need screening details.';
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dob-cart-item__cta-button';
    button.textContent = filledCount > 0 ? 'Continue screening details' : 'Provide screening details';
    button.addEventListener('click', function (event) {
      // Stop bubble: the theme's drawer-close listener on window checks
      // evt.target.closest('.drawer'); our DOM mutation below detaches
      // this button before that runs, which would close the drawer.
      event.stopPropagation();
      openCartEditForm(lineEl, slotsNeeded);
    });

    wrap.appendChild(heading);
    wrap.appendChild(label);
    wrap.appendChild(button);
    return wrap;
  }

  function buildSummary(props, slotsNeeded, lineEl) {
    const wrap = document.createElement('div');
    wrap.className = 'dob-cart-item__summary';

    const header = document.createElement('div');
    header.className = 'dob-cart-item__summary-header';
    const heading = document.createElement('strong');
    heading.textContent = 'Screening details';
    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'dob-cart-item__summary-edit';
    edit.textContent = 'Edit';
    edit.addEventListener('click', function (event) {
      // See note on the CTA button: stopPropagation prevents the theme's
      // window-level drawer-close listener from seeing a detached target
      // after our DOM mutation, which would close the drawer.
      event.stopPropagation();
      openCartEditForm(lineEl, slotsNeeded);
    });
    header.appendChild(heading);
    header.appendChild(edit);
    wrap.appendChild(header);

    const list = document.createElement('ul');
    list.className = 'dob-cart-item__summary-list';

    for (let i = 1; i <= slotsNeeded; i++) {
      const vals = readSlotValues(props, i);
      const entry = document.createElement('li');
      entry.className = 'dob-cart-item__summary-entry';

      if (slotsNeeded > 1) {
        const tag = document.createElement('span');
        tag.className = 'dob-cart-item__summary-tag';
        tag.textContent = 'Test ' + i;
        entry.appendChild(tag);
      }

      const name = document.createElement('span');
      name.className = 'dob-cart-item__summary-name';
      name.textContent = vals.name;
      entry.appendChild(name);

      const dob = document.createElement('span');
      dob.className = 'dob-cart-item__summary-dob';
      dob.textContent = formatDOB(vals.dob);
      entry.appendChild(dob);

      list.appendChild(entry);
    }

    wrap.appendChild(list);
    return wrap;
  }

  function openCartEditForm(lineEl, slotsNeeded) {
    const inner = lineEl.querySelector('[data-dob-cart-inner]');
    const propsScript = lineEl.querySelector('script[data-dob-line-properties]');
    let propsArr = [];
    try { propsArr = JSON.parse(propsScript.textContent.trim()); } catch (e) { propsArr = []; }
    const props = arrayToPropMap(propsArr);

    // Cart drawer (#CartDrawerForm) and cart page (#CartPageForm) both wrap
    // their line items in a <form>. Nesting our edit <form> inside that is
    // invalid HTML and causes inconsistent submit behavior on Enter and
    // autofill. When we're inside an outer form, render a <div> and rely on
    // the explicit Save button + Enter-key interception. For future cart
    // contexts that aren't wrapped in a form, use a real <form> so Enter-key
    // submission works natively.
    const isInsideForm = !!lineEl.closest('form');
    const form = document.createElement(isInsideForm ? 'div' : 'form');
    form.className = 'dob-cart-item__form';
    if (!isInsideForm) {
      form.noValidate = true;
    }

    for (let i = 1; i <= slotsNeeded; i++) {
      const keys = slotKeys(i);
      const vals = readSlotValues(props, i);
      const slot = document.createElement('div');
      slot.className = 'dob-cart-item__form-slot';
      slot.dataset.dobSlot = String(i);

      if (slotsNeeded > 1) {
        const label = document.createElement('div');
        label.className = 'dob-cart-item__form-slot-label';
        label.textContent = 'Test ' + i + ' of ' + slotsNeeded;
        slot.appendChild(label);
      }

      slot.appendChild(buildCartField('name', keys.name, 'Patient name', vals.name, i, lineEl));
      slot.appendChild(buildCartField('dob', keys.dob, 'Date of birth (DD/MM/YYYY)', vals.dob, i, lineEl));

      form.appendChild(slot);
    }

    const actions = document.createElement('div');
    actions.className = 'dob-cart-item__form-actions';

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'dob-cart-item__cancel';
    cancel.textContent = 'Cancel';
    cancel.addEventListener('click', function (event) {
      event.stopPropagation();
      renderCartLineState(lineEl, slotsNeeded);
    });

    const save = document.createElement('button');
    // type=button regardless of wrapper: in <div> mode there's no inner
    // <form> to submit; in <form> mode we want the wrapper's submit handler
    // (below) to be the single source of truth, not the button's default.
    save.type = 'button';
    save.className = 'dob-cart-item__save';
    save.textContent = 'Save';
    save.addEventListener('click', function (event) {
      event.stopPropagation();
      saveCartLineForm(form, lineEl, slotsNeeded);
    });

    actions.appendChild(cancel);
    actions.appendChild(save);
    form.appendChild(actions);

    if (isInsideForm) {
      // Inputs sit inside the outer cart <form>. Pressing Enter would submit
      // that form (full /cart POST + reload). Intercept Enter on our inputs
      // and call save instead.
      form.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter') return;
        if (!event.target.matches('input')) return;
        event.preventDefault();
        event.stopPropagation();
        saveCartLineForm(form, lineEl, slotsNeeded);
      });
    } else {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        saveCartLineForm(form, lineEl, slotsNeeded);
      });
    }

    inner.innerHTML = '';
    inner.appendChild(form);

    const firstEmpty = Array.from(form.querySelectorAll('input')).find(function (i) {
      return !(i.value || '').trim();
    });
    (firstEmpty || form.querySelector('input')).focus();

    // Opening the edit form takes this line out of "summary" state, which the
    // wrapper's lock predicate counts as incomplete. Re-sync so the express
    // area locks while the customer is editing (until Save → cart re-render
    // → bootstrap → sync → unlocks again if all lines now have summaries).
    syncAllCartCheckoutWrappers();
  }

  function buildCartField(type, propKey, labelText, value, slotIndex, lineEl) {
    const wrap = document.createElement('div');
    wrap.className = 'dob-cart-item__form-field';

    const fieldId = 'dob-cart-' + type + '-' + slotIndex + '-' + lineEl.dataset.dobLineKey;
    const label = document.createElement('label');
    label.htmlFor = fieldId;
    label.textContent = labelText;

    const input = document.createElement('input');
    input.type = type === 'dob' ? 'date' : 'text';
    input.id = fieldId;
    input.name = propKey;
    input.value = value;
    input.dataset.dobField = type;
    if (type === 'dob') {
      input.min = '1900-01-01';
      const today = new Date();
      input.max = today.toISOString().slice(0, 10);
    }
    input.addEventListener('blur', function () { validateField(input); });
    input.addEventListener('input', function () { clearFieldError(input); });

    const error = document.createElement('span');
    error.className = 'dob-collection__error';
    error.setAttribute('data-dob-error', '');
    error.hidden = true;

    wrap.appendChild(label);
    wrap.appendChild(input);
    wrap.appendChild(error);
    return wrap;
  }

  function saveCartLineForm(form, lineEl, slotsNeeded) {
    const inputs = Array.from(form.querySelectorAll('input'));
    let allValid = true;
    inputs.forEach(function (input) {
      if (!validateField(input)) allValid = false;
    });
    if (!allValid) {
      const firstInvalid = inputs.find(function (i) { return i.getAttribute('aria-invalid') === 'true'; });
      if (firstInvalid) firstInvalid.focus();
      return;
    }

    // /cart/change.js with `properties` REPLACES the line item's full
    // properties map, so we must merge ourselves to preserve any non-DOB
    // properties (file uploads, gift metadata, app-set keys).
    const propsScript = lineEl.querySelector('script[data-dob-line-properties]');
    let existingProps = {};
    if (propsScript) {
      let propsArr = [];
      try { propsArr = JSON.parse(propsScript.textContent.trim()); } catch (e) { propsArr = []; }
      existingProps = arrayToPropMap(propsArr);
    }

    // Carry forward every existing property except DOB-managed ones. Stripping
    // is unconditional (not slot-count-aware) so cleared slots and stale keys
    // from prior slot counts don't linger after save.
    const properties = {};
    Object.keys(existingProps).forEach(function (k) {
      if (!isDobManagedKey(k)) properties[k] = existingProps[k];
    });
    inputs.forEach(function (input) {
      const v = (input.value || '').trim();
      if (v) properties[input.name] = v;
    });

    const key = lineEl.dataset.dobLineKey;
    const lineIndex = getCartLineDomIndex(lineEl);
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, properties: properties }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart change responded ' + res.status);
      return res.json();
    }).then(function (cart) {
      const updatedLine = findUpdatedCartLine(cart, lineEl, properties, lineIndex);
      if (!updatedLine || !updatedLine.key) return null;
      const attrProperties = {};
      for (let i = 1; i <= slotsNeeded; i++) {
        const keys = slotKeys(i);
        attrProperties[keys.name] = properties[keys.name] || '';
        attrProperties[keys.dob] = properties[keys.dob] || '';
      }
      return writeCartAttributesForLine({
        sku: (updatedLine.variant && updatedLine.variant.sku) || updatedLine.sku || lineEl.dataset.dobLineSku,
        handle: (updatedLine.product && updatedLine.product.handle) || updatedLine.handle || lineEl.dataset.dobLineHandle,
        lineKey: updatedLine.key,
        properties: attrProperties,
      }).catch(function (err) {
        console.warn('[dob-collection] cart-side DOB attribute write failed:', err);
      });
    }).then(function () {
      refreshCartFragments();
    }).catch(function (err) {
      console.warn('[dob-collection] save failed:', err);
      alert('Could not save screening details. Please try again.');
    });
  }

  function getCartLineDomIndex(lineEl) {
    const root = lineEl.closest('form') || document;
    const cartItem = lineEl.closest('.cart__item[data-key]');
    if (!cartItem) return -1;
    // -1 is safe: findUpdatedCartLine then skips the index hint and uses the scan fallback.
    return Array.from(root.querySelectorAll('.cart__item[data-key]')).indexOf(cartItem);
  }

  function findUpdatedCartLine(cart, lineEl, properties, lineIndex) {
    const items = (cart && cart.items) || [];
    const indexedLine = lineIndex >= 0 ? items[lineIndex] : null;
    if (indexedLine && cartLineMatchesSubmittedProperties(indexedLine, lineEl, properties)) {
      return indexedLine;
    }
    return items.find(function (item) {
      return cartLineMatchesSubmittedProperties(item, lineEl, properties);
    }) || null;
  }

  function cartLineMatchesSubmittedProperties(item, lineEl, properties) {
    const variantId = lineEl.dataset.dobLineVariantId;
    if (variantId && String(item.variant_id) !== String(variantId)) return false;
    return dobPropertiesEqual(item.properties || {}, properties);
  }

  function dobPropertiesEqual(itemProperties, submittedProperties) {
    const itemMap = Array.isArray(itemProperties) ? arrayToPropMap(itemProperties) : itemProperties;
    const keys = Object.keys(submittedProperties).filter(isDobManagedKey);
    return keys.every(function (key) {
      return String(itemMap[key] || '') === String(submittedProperties[key] || '');
    });
  }

  function refreshCartFragments() {
    // Prefer the theme's own cart refresh hook (theme.js listens for `cart:build`
    // on CartForm.init and calls this.buildCart()). This re-renders both the
    // drawer (#CartDrawer) and the cart page (#CartPageForm) via theme.cart
    // markup, keeping subtotals, savings and quantity selectors in sync.
    // Falls back to /cart?view=ajax fetch if the event has no listener.
    let handled = false;
    try {
      document.dispatchEvent(new CustomEvent('cart:build'));
      handled = !!document.querySelector('#CartDrawerForm, #CartPageForm');
    } catch (e) {
      handled = false;
    }
    if (handled) {
      // The MutationObserver on [data-products] will re-bootstrap once theme
      // swaps in fresh markup. Nothing else to do.
      return;
    }
    fetch('/cart?view=ajax', {
      headers: { 'Accept': 'text/html' },
    }).then(function (r) { return r.text(); }).then(function (html) {
      const drawerHost = document.querySelector('#CartDrawer [data-products]');
      const pageHost = document.querySelector('#CartPageForm [data-products]');
      const target = drawerHost || pageHost;
      if (target) target.innerHTML = html;
      bootstrapCart();
    }).catch(function () {});
  }

  // Cart-side parallel to syncPaymentAreaInertState. The PDP version locks
  // [data-dob-buy-fieldset] within a product form. This one locks
  // [data-dob-cart-checkout-wrap] within the slide-cart or /cart page.
  //
  // Predicate mirrors blockIfIncomplete (used by the click intercept further
  // below): "any DOB-required cart line is incomplete" = "any [data-dob-cart-
  // line] without a .dob-cart-item__summary child". CTA, loading, and open-
  // form states all count as incomplete.
  function syncCartPaymentAreaInertState(wrapEl) {
    const required = wrapEl.dataset.dobCartRequired === 'true';

    const expressContainers = wrapEl.querySelectorAll(
      '.shopify-payment-button, .additional-checkout-buttons'
    );
    if (!expressContainers.length) return;

    let shouldLock = false;
    if (required) {
      const enclosingForm = wrapEl.closest('form');
      const lineRoot = enclosingForm || document;
      const lines = Array.from(lineRoot.querySelectorAll('[data-dob-cart-line]'));
      shouldLock = lines.some(function (lineEl) {
        return !lineEl.querySelector('.dob-cart-item__summary');
      });
    }

    expressContainers.forEach(function (container) {
      if (shouldLock) {
        container.inert = true;
        container.setAttribute('aria-disabled', 'true');
        container.style.opacity = '0.5';
        container.style.cursor = 'not-allowed';
      } else {
        container.inert = false;
        container.removeAttribute('aria-disabled');
        container.style.opacity = '';
        container.style.cursor = '';
      }
    });
  }

  function syncAllCartCheckoutWrappers() {
    document.querySelectorAll('[data-dob-cart-checkout-wrap]').forEach(
      syncCartPaymentAreaInertState
    );
  }

  function attachCheckoutIntercept(wrapEl) {
    if (wrapEl.dataset.dobCheckoutIntercepted === 'true') return;
    wrapEl.dataset.dobCheckoutIntercepted = 'true';

    const context = wrapEl.dataset.dobCartContext;

    // Returns true if the event was blocked, false if checkout/submit is
    // allowed through. Used by both the click intercept (on the wrapEl) and
    // the submit intercept (on the enclosing cart form).
    function blockIfIncomplete(event) {
      const required = wrapEl.dataset.dobCartRequired === 'true';
      if (!required) return false;

      // Scope to lines inside this wrapper's enclosing form (cart drawer vs cart page).
      const enclosingForm = wrapEl.closest('form');
      const lineRoot = enclosingForm || document;
      const lines = Array.from(lineRoot.querySelectorAll('[data-dob-cart-line]'));

      // A line is incomplete unless it has reached the summary state. Catches
      // loading, CTA and form-open states.
      const incomplete = lines.find(function (lineEl) {
        return !lineEl.querySelector('.dob-cart-item__summary');
      });
      if (!incomplete) return false;

      event.preventDefault();
      event.stopImmediatePropagation();
      clarityEvent('dob_form_validation_failed', {
        placement: context,
        product_handle: incomplete.dataset.dobLineHandle,
        slots_needed: 0,
        slots_filled: 0,
        mode: 'required',
      });

      // Loading state: slot count not resolved yet, so we can't safely
      // render a form (would render 1 slot for a possibly-multi-slot product
      // and any partial input would be wiped when renderCartLineState swaps
      // in the resolved markup). Surface a notice instead.
      const isLoading = !!incomplete.querySelector('.dob-cart-item__loading');
      if (isLoading) {
        showCartLoadingNotice(incomplete);
        incomplete.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      }

      const perUnit = slotCountCache.get(incomplete.dataset.dobLineHandle) || 1;
      const qty = parseInt(incomplete.dataset.dobLineQuantity, 10) || 1;
      const slotsNeeded = perUnit * qty;

      // If the form is already open, don't rebuild — that would wipe any
      // values the customer has already started typing. Just re-show the
      // warning and refocus.
      const innerEl = incomplete.querySelector('[data-dob-cart-inner]');
      const existingForm = innerEl && innerEl.querySelector('.dob-cart-item__form');
      if (!existingForm) {
        openCartEditForm(incomplete, slotsNeeded);
      }

      showCartFormWarning(incomplete, 'Screening details are required before checkout.');

      const firstEmpty = Array.from(incomplete.querySelectorAll('.dob-cart-item__form input')).find(function (i) {
        return !(i.value || '').trim();
      });
      if (firstEmpty) firstEmpty.focus();

      incomplete.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    }

    wrapEl.addEventListener('click', function (event) {
      const triggerSel = 'button, [role="button"], shop-pay-button, [is^="shopify-"]';
      if (!event.target.closest(triggerSel)) return;
      blockIfIncomplete(event);
    }, true);

    // Submit guard on the enclosing cart form. Without this, pressing Enter
    // in a cart qty input submits CartDrawerForm/CartPageForm straight to
    // /cart (and possibly through to checkout, depending on which submit
    // button is the implicit default in the form), bypassing the click
    // intercept above. Capture-phase + stopImmediate so theme cart handlers
    // don't run on a blocked submit.
    const enclosingForm = wrapEl.closest('form');
    if (enclosingForm && enclosingForm.dataset.dobCheckoutSubmitWired !== 'true') {
      enclosingForm.dataset.dobCheckoutSubmitWired = 'true';
      enclosingForm.addEventListener('submit', function (event) {
        blockIfIncomplete(event);
      }, true);
    }
  }

  function showCartFormWarning(lineEl, message) {
    const inner = lineEl.querySelector('[data-dob-cart-inner]');
    const form = inner && inner.querySelector('.dob-cart-item__form');
    if (!form) return;

    // Remove any previous warning so the appearance animation re-runs.
    const existing = form.querySelector('.dob-cart-item__form-warning');
    if (existing) existing.remove();

    const warning = document.createElement('div');
    warning.className = 'dob-cart-item__form-warning';
    warning.setAttribute('role', 'alert');

    const icon = document.createElement('span');
    icon.className = 'dob-cart-item__form-warning-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" focusable="false"><path d="M12 2 1 21h22L12 2zm0 4.5L19.6 19H4.4L12 6.5zm-1 4.5v5h2v-5h-2zm0 6v2h2v-2h-2z"/></svg>';

    const text = document.createElement('span');
    text.className = 'dob-cart-item__form-warning-text';
    text.textContent = message;

    warning.appendChild(icon);
    warning.appendChild(text);

    form.insertBefore(warning, form.firstChild);
  }

  function showResolvingNotice(formEl) {
    if (formEl.querySelector('.dob-collection__resolving-notice')) return;
    const notice = document.createElement('p');
    notice.className = 'dob-collection__resolving-notice';
    notice.setAttribute('role', 'alert');
    notice.textContent = 'Confirming screening requirements — please try again in a moment.';
    formEl.appendChild(notice);
  }

  function clearResolvingNotice(formEl) {
    const existing = formEl.querySelector('.dob-collection__resolving-notice');
    if (existing) existing.remove();
  }

  function showCartLoadingNotice(lineEl) {
    const loadingEl = lineEl.querySelector('.dob-cart-item__loading');
    if (!loadingEl) return;
    if (loadingEl.querySelector('.dob-cart-item__loading-notice')) return;

    const notice = document.createElement('p');
    notice.className = 'dob-cart-item__loading-notice';
    notice.setAttribute('role', 'alert');
    notice.textContent = 'Screening details are still loading. Please wait a moment and retry.';
    loadingEl.appendChild(notice);
  }

  function bootstrapAll() {
    bootstrap();
    bootstrapCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapAll);
  } else {
    bootstrapAll();
  }

  document.addEventListener('shopify:section:load', bootstrapAll);

  if (document.querySelector('#CartPageForm')) {
    scheduleCartAttributeGc();
  }

  document.addEventListener('drawerOpen.CartDrawer', function () {
    scheduleCartAttributeGc();
  });

  document.addEventListener('ajaxProduct:added', function (event) {
    const addedProduct = event.detail && event.detail.product;
    const formEl = findRecordedDobFormForAddedProduct(addedProduct);
    if (!formEl) return;
    promotePdpFormAfterAdd(formEl, addedProduct);
  });

  document.addEventListener('product-form:submit:success', function (event) {
    const addedProduct = event.detail && event.detail.response;
    if (!addedProduct) return;

    const formEl = findPdpFormForSubmittedEvent(event, addedProduct);
    if (!formEl) return;
    promotePdpFormAfterAdd(formEl, addedProduct);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      beaconPendingCartAttributeCleanup();
      return;
    }
    document.querySelectorAll(SELECTORS.form).forEach(function (formEl) {
      const dirty = Array.from(formEl.querySelectorAll(SELECTORS.field)).some(function (field) {
        return field.dataset.dobDirty === 'true';
      });
      if (dirty) flushPdpCartAttributeWriteNow(formEl);
    });
  });

  window.addEventListener('pagehide', beaconPendingCartAttributeCleanup);

  // Observe cart DOM mutations to re-bootstrap after the theme's AJAX cart refresh.
  function observeCartContainer(selector) {
    const target = document.querySelector(selector);
    if (!target) return;
    new MutationObserver(function () { bootstrapCart(); }).observe(target, { childList: true, subtree: true });
  }
  observeCartContainer('#CartDrawer [data-products]');
  observeCartContainer('#CartPageForm [data-products]');

  window.addEventListener('beforeunload', function () {
    document.querySelectorAll(SELECTORS.form).forEach(function (formEl) {
      const fields = Array.from(formEl.querySelectorAll(SELECTORS.field));
      const filled = fields.filter(function (f) { return (f.value || '').trim(); }).length;
      if (filled > 0 && filled < fields.length) {
        clarityEvent('dob_form_abandoned', buildEventProperties(formEl));
      }
    });
  });
})();
